'use client';

import { useRef } from 'react';
import { isEmpty, join, map, size } from 'lodash';
import { useMediaQuery } from 'usehooks-ts';
import { CloseIcon, PaperClipIcon } from '@/icons';

type IFileUploadProps = {
  label: string;
  id: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  errorMessage?: string;
  hasError?: boolean;
  accept?: string;
  multiple?: boolean;
  value?: FileList;
};

export const FileUpload = ({
  label,
  id,
  onChange,
  errorMessage,
  hasError,
  accept,
  multiple = false,
  value
}: IFileUploadProps) => {
  const isMobile = useMediaQuery('(max-width: 1023px)');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fileNames =
    size(value) > 0
      ? join(
          map(value, (file) => file.name),
          ', '
        )
      : undefined;

  const buttonLabel = isMobile ? fileNames || 'Adicionar anexo' : 'Anexo';

  const handleClear = () => {
    onChange({ target: { value: null, files: null } } as any);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div id="name_wrapper" data-testid="name_wrapper" className="flex w-full flex-col gap-2">
      <div className="text-sm font-regular uppercase text-blue-400 lg:text-grey-900">
        <span className="hidden lg:block">{label}</span>
        <div className="mt-2 flex items-center gap-2">
          <label htmlFor={id}>
            <div
              role="button"
              tabIndex={0}
              aria-label={label}
              className="flex h-10 w-fit items-center justify-center gap-2 py-3 text-xs font-semibold tracking-widest focus:outline-none focus:ring-2 lg:border lg:border-grey-500 lg:bg-grey-300 lg:px-4 lg:focus:ring-grey-500 lg:focus:ring-offset-2 lg:focus:ring-offset-grey-300"
            >
              {isMobile && <PaperClipIcon className="size-4" />}
              <span className="line-clamp-1 flex-1 cursor-pointer">{buttonLabel}</span>
            </div>
          </label>
          <p className="hidden normal-case italic text-grey-800 lg:block">
            {!isEmpty(fileNames) ? (
              <span className="flex items-center gap-2">
                {fileNames}
                <button
                  type="button"
                  onClick={handleClear}
                  className="text-grey-800 hover:text-grey-900"
                >
                  <CloseIcon width={24} height={24} />
                  <span className="sr-only">Remover anexo</span>
                </button>
              </span>
            ) : (
              <label htmlFor={id}>Limite máximo de 20MB (jpg, png, doc, pdf, zip e outros)</label>
            )}
          </p>
        </div>
      </div>
      <span className="hidden">
        <input
          id={id}
          type="file"
          onChange={onChange}
          accept={accept}
          multiple={multiple}
          ref={fileInputRef}
        />
      </span>
      {hasError && errorMessage && (
        <p id={`${id}_error_message`} className="text-xs font-regular text-red">
          {errorMessage}
        </p>
      )}
    </div>
  );
};
